import { HStack } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../../../shared/hooks/useApi";
import type { ResponseOf } from "../../../../../core/api";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
};

type HoursOverrideResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/agency_member/:agencyMemberId/caregiver/:caregiverId/hours_overrides"
>;
type HoursOverride = HoursOverrideResponse["overrides"][number];
type SortKey = "officeName" | "startTime" | "endTime" | "hoursPerWeek";

export default function CaregiverProfileHoursOverridesSection(props: Props) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const queryKey = ["caregiverProfileModal", props.caregiverId, "hoursOverrides"];
  const overridesQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get(
        "/agencies/:agencyId/agency_member/:agencyMemberId/caregiver/:caregiverId/hours_overrides",
        {
          path: { caregiverId: props.caregiverId },
          query: {},
        }
      ),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey });
  const removeMutation = useMutation({
    mutationFn: (override: HoursOverride) =>
      api.post(
        "/agencies/:agencyId/agency_member/:agencyMemberId/caregiver/:caregiverId/hours_overrides/:overrideId",
        { path: { caregiverId: props.caregiverId, overrideId: override.id } }
      ),
    onSuccess: invalidate,
  });
  const rows = overridesQuery.data?.overrides ?? [];
  const columns: Array<EntityProfileDataTableColumn<HoursOverride, SortKey>> = [
    {
      key: "office",
      header: "Office",
      sortKey: "officeName",
      sortValue: (row) => row.officeName,
      render: (row) => row.officeName,
    },
    {
      key: "startTime",
      header: "Start time",
      sortKey: "startTime",
      sortValue: (row) => String(row.startTime),
      render: (row) => dateFormatter.toDate(row.startTime),
    },
    {
      key: "endTime",
      header: "End time",
      sortKey: "endTime",
      sortValue: (row) => String(row.endTime),
      render: (row) => dateFormatter.toDate(row.endTime),
    },
    {
      key: "hoursPerWeek",
      header: "Hours per week",
      sortKey: "hoursPerWeek",
      sortValue: (row) => row.hoursPerWeek,
      render: (row) => row.hoursPerWeek,
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <HStack spacing={2}>
          <EntityModalButton
            disabled={props.caregiver === undefined}
            iconClassName="fa fa-pencil"
            kind="blue"
            size="xs"
            onClick={() =>
              props.caregiver === undefined
                ? undefined
                : props.actions.openCaregiverHoursOverride({
                    caregiver: props.caregiver,
                    data: row,
                    onDone: invalidate,
                  })
            }
          >
            Edit
          </EntityModalButton>
          <EntityModalButton
            iconClassName="fa fa-trash"
            isLoading={removeMutation.isPending}
            kind="red"
            size="xs"
            onClick={() => removeMutation.mutate(row)}
          >
            Remove
          </EntityModalButton>
        </HStack>
      ),
    },
  ];

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load hours overrides."
      isError={overridesQuery.isError}
      isLoading={overridesQuery.isLoading}
      loadingLabel="Loading hours overrides"
      onRetry={() => void overridesQuery.refetch()}
    >
      <HStack justifyContent="flex-end" mb={3}>
        <EntityModalButton
          disabled={props.caregiver === undefined}
          iconClassName="fa fa-plus"
          kind="blue"
          size="sm"
          onClick={() =>
            props.caregiver === undefined
              ? undefined
              : props.actions.openCaregiverHoursOverride({
                  caregiver: props.caregiver,
                  onDone: invalidate,
                })
          }
        >
          Create override
        </EntityModalButton>
      </HStack>
      <EntityProfileDataTable
        columns={columns}
        defaultPageSize={10}
        defaultSort={{ key: "officeName", direction: "asc" }}
        getRowKey={(row) => row.id}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={rows}
        showEmptyState={false}
      />
    </EntityProfileQuerySection>
  );
}
